'use client';

import Link from 'next/link';
import Image from 'next/image';
import { MapPin, Star } from 'lucide-react';
import type { Hotel } from '../types/hotels.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  SimilarHotels — extracted from HotelDetail for file-size compliance       */
/* -------------------------------------------------------------------------- */

interface SimilarHotelsProps {
  hotels: Hotel[];
  formatMoney: (n: number | null | undefined) => string;
}

export function SimilarHotels({ hotels, formatMoney }: SimilarHotelsProps) {
  const { t } = useTranslation();
  if (hotels.length === 0) return null;

  return (
    <>
      <hr className="border-hms-border" />
      <div id="hotels-detail-similar" className="space-y-4">
        <h2 className="text-lg font-semibold text-hms-text">{t('hotelSearch.similarHotels')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {hotels.map((sh) => (
            <Link
              key={sh.id}
              id={`hotels-detail-similar-card-${sh.id}`}
              href={`/hotels/${sh.id}`}
              className="flex gap-3 rounded-xl border border-hms-border bg-hms-card p-3 hover:border-hms-gold/30 transition-colors"
            >
              <div className="relative h-16 w-20 shrink-0 rounded-lg overflow-hidden">
                <Image src={sh.image} alt={sh.name} fill sizes="80px" className="object-cover" unoptimized />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-hms-text truncate">{sh.name}</p>
                <p className="text-[11px] text-hms-textDim mt-0.5 flex items-center gap-1">
                  <MapPin size={10} /> {sh.location}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-hms-gold">
                    <Star size={10} className="fill-hms-gold" /> {sh.rating}
                  </span>
                  <span className="text-xs font-semibold text-hms-text">
                    {formatMoney(sh.price)}
                    <span className="text-[10px] font-normal text-hms-textDim">{t('hotelSearch.perNight')}</span>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
